import { useContext } from 'react';

import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import { ListContext } from '@contexts/ListContext';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { forceReloadModels } from '@hooks/API/Contact/UseContactsList';

import { forceReloadAgencyModels } from './UseAgencyModels';

const deleteAgency = ({ ModelAgencyID, AgencyID, ModelID }) => Axios.delete(`/modelagencies/${ModelAgencyID}`);

const addAgency = (data) => Axios.post('/modelagencies', data);

const editAgency = (data) => Axios.put(`/modelagencies`, data);

const resolveLabel = (item, modelAgencyLabels) => ({
    ...item,
    Label: modelAgencyLabels.find((l) => l.ID === item.ModelAgencyLabelID) || null,
});

const useContactAgencies = (ID) => {
    const { cachedList } = useContext(ListContext);
    const modelAgencyLabels = cachedList.modelAgencyLabels.items;
    const disableQuery = !ID || isNaN(parseInt(ID));
    const intID = !disableQuery ? parseInt(ID) : null;
    return useQuery({
        queryKey: ['contactAgencies', intID],
        queryFn: () => Axios.get(`/models/getAgencies/${intID}`),
        select: (data) => data?.map((item) => resolveLabel(item, modelAgencyLabels)),
        staleTime: 60000,
        cacheTime: 60000,
        enabled: !disableQuery,
    });
};

const forceReloadContactAgencies = (ID) => {
    const intID = Number(ID);
    queryClient.invalidateQueries({ queryKey: ['contactAgencies', intID] });
};

const useRemoveAgency = () => {
    return useMutation({
        mutationFn: deleteAgency,
        onSuccess: (data, variables) => {
            forceReloadContact(variables.ModelID);
            forceReloadContactAgencies(variables.ModelID);
            forceReloadAgencyModels(variables.AgencyID);
            forceReloadModels();
        },
    });
};

const useAddAgency = () => {
    return useMutation({
        mutationFn: addAgency,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['contactAgencies', data.ModelID] });
            forceReloadContact(data.ModelID);
            forceReloadContactAgencies(data.ModelID);
            forceReloadAgencyModels(data.AgencyID);
            forceReloadModels();
        },
    });
};

const useEditAgency = () => {
    return useMutation({
        mutationFn: editAgency,
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['contactAgencies', data.ModelID] });
            forceReloadContact(data.ModelID);
            forceReloadContactAgencies(data.ModelID);
            forceReloadAgencyModels(data.AgencyID);
            forceReloadModels();
        },
    });
};

export { useContactAgencies, useRemoveAgency, useAddAgency, useEditAgency };
